import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import AuthenticatedLayout from '../layouts/AuthenticatedLayout';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import InviteAccept from '../features/auth/components/InviteAccept';
import MfaEnroll from '../features/auth/components/MfaEnroll';
import MfaChallenge from '../features/auth/components/MfaChallenge';
import Spinner from '../components/ui/Spinner';
import { OrganizationProvider } from '../contexts/OrganizationContext';
import { CrmProviders } from '../contexts/CrmProviders';
import { NotificationsProvider } from '../contexts/NotificationsContext';
import { OnboardingProvider } from '../features/onboarding/OnboardingProvider';
import { PostHogOrganizationAnalytics } from '../components/analytics/PostHogOrganizationAnalytics';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import { isInviteRoute } from '../services/modules/organizationService';
import { isPartnerApplyRoute } from '../utils/routeUtils';
import { PartnerPublicApplyPage } from '../features/partner/PartnerPublicApplyPage';
import { OAuthAuthorizePage } from '../features/oauth/OAuthAuthorizePage';
import { isOAuthAuthorizeRoute } from '../features/oauth/authorizeParams';

const AppRouter: React.FC = () => {
  const { isAuthenticated, authPage, isAuthLoading, mfaGate } = useAuth();

  if (isAuthLoading) {
    return (
      <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-50 transition-opacity duration-300">
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-75" />
          <div className="relative bg-white p-4 rounded-full shadow-sm flex items-center justify-center">
            <Spinner color="blue" size="lg" />
          </div>
        </div>
        <h3 className="text-base font-medium text-gray-900 animate-pulse">
          Carregando Adlitics...
        </h3>
        <p className="mt-2 text-sm text-gray-500 max-w-xs mx-auto text-center">
          Verificando suas credenciais.
        </p>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (authPage === 'invite' || isInviteRoute()) return <InviteAccept />;
    if (isPartnerApplyRoute()) return <PartnerPublicApplyPage />;
    if (authPage === 'login') return <LoginPage />;
    return <RegisterPage />;
  }

  if (mfaGate === 'checking') {
    return (
      <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-50">
        <Spinner color="blue" size="lg" />
        <p className="mt-4 text-sm text-gray-500">Verificando autenticação em 2 etapas...</p>
      </div>
    );
  }

  if (mfaGate === 'enroll') return <MfaEnroll />;
  if (mfaGate === 'challenge') return <MfaChallenge />;

  // Partner OAuth consent: standalone page (keeps /oauth/authorize?... for the code redirect).
  if (isOAuthAuthorizeRoute()) {
    return (
      <OrganizationProvider>
        <OAuthAuthorizePage />
      </OrganizationProvider>
    );
  }

  // OrganizationProvider único: sair de /convite não remonta o tenant (evita flicker).
  return (
    <OrganizationProvider>
      <PostHogOrganizationAnalytics />
      {isInviteRoute() ? (
        <InviteAccept />
      ) : (
        <NotificationsProvider>
          <CrmProviders>
            <ErrorBoundary>
              <OnboardingProvider>
                <AuthenticatedLayout />
              </OnboardingProvider>
            </ErrorBoundary>
          </CrmProviders>
        </NotificationsProvider>
      )}
    </OrganizationProvider>
  );
};

export default AppRouter;
